import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import {
  AlertTriangle,
  ArrowRightLeft,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardPaste,
  Eye,
  MessageSquare,
  Mic,
  Timer,
  Trophy,
  Type,
  User,
  X,
} from "lucide-react";
import { interpreterScore, pluriel } from "@repo/utils";
import type { CheatingMeta, CheatingStats } from "@repo/utils";
import type {
  CompetenceScore,
  CritereDetail,
  EntStructExchange,
  EntStructMessage,
  NotationParModele,
  SousCompetenceScore,
} from "./entStruct.types";
import {
  getMessagesFromReponses,
  libelleQualiteReponse,
} from "./entStruct.utils";

export function ScoreGlobalHeader({
  obtenu,
  max,
  brutObtenu,
  brutMax,
  tricheDetectee,
  statsCheating,
}: {
  obtenu: number;
  max: number;
  brutObtenu: number;
  brutMax: number;
  tricheDetectee?: boolean;
  statsCheating?: CheatingStats;
}) {
  const pourcentage = max > 0 ? Math.round((obtenu / max) * 100) : 0;
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="p-6 rounded-surface text-center">
      <div className="flex items-center justify-center gap-2 mb-2">
        <Trophy className="h-6 w-6 text-primary" />
        <p className="text-sm font-medium text-muted-foreground">
          Matching Compétences
        </p>
      </div>
      <div className="flex flex-col items-center gap-2">
        <p
          className={`text-7xl font-bold ${pourcentage >= 61 ? "text-etat-fait" : pourcentage >= 21 ? "text-etat-alerte" : "text-etat-erreur"}`}
        >
          {pourcentage}%
        </p>
        {tricheDetectee && (
          <div
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pastille bg-etat-alerte-surface text-etat-alerte-contenu text-sm font-medium whitespace-nowrap cursor-default">
              <AlertTriangle className="h-4 w-4" />
              Potentielle triche détectée
            </span>
            {showTooltip && statsCheating && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 bg-popover border rounded-surface p-3 shadow-lg min-w-[260px] text-sm">
                <p className="font-semibold text-etat-alerte-contenu mb-2">
                  Raisons de la détection
                </p>
                <ul className="space-y-1.5 text-xs text-muted-foreground">
                  {statsCheating.nombrePastes > 0 && (
                    <li className="flex items-center gap-2 text-etat-erreur font-medium">
                      <ClipboardPaste className="h-3 w-3" />
                      {statsCheating.nombrePastes} copier-coller
                      {statsCheating.nombrePastes > 1 ? "s" : ""} détecté
                      {statsCheating.nombrePastes > 1 ? "s" : ""}
                    </li>
                  )}
                  {statsCheating.totalChangementsOnglet > 0 && (
                    <li className="flex items-center gap-2">
                      <ArrowRightLeft className="h-3 w-3" />
                      {pluriel(statsCheating.totalChangementsOnglet, "changement")}
                      d'onglet
                    </li>
                  )}
                  {statsCheating.messagesVitesseSuspecte > 0 && (
                    <li className="flex items-start gap-2 text-etat-alerte">
                      <Type className="h-3 w-3 shrink-0" />
                      <span>
                        {pluriel(
                          statsCheating.messagesVitesseSuspecte,
                          "réponse",
                        )}
                        à vitesse de frappe anormale (max :{" "}
                        {statsCheating.vitesseFrappeMax.toFixed(1)} car/s)
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-2">
        Score brut : {brutObtenu}/{brutMax} points
      </p>
    </div>
  );
}

function IconeCouverture({ covered }: { covered: boolean }) {
  return covered ? (
    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-etat-fait-surface">
      <Check className="h-4 w-4 text-etat-fait" />
    </div>
  ) : (
    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-surface-3">
      <X className="h-4 w-4 text-ardoise" />
    </div>
  );
}

function TableCriteres({
  criteres,
  notationsParModele,
}: {
  criteres: CritereDetail[];
  notationsParModele?: NotationParModele[];
}) {
  // Prop fournie ⇒ contexte admin multi-modèles : colonne Points retirée, une
  // colonne par modèle ajoutée. Prop absente (portail-client) : comportement
  // inchangé (colonne Points).
  const isComparaisonModeles = notationsParModele !== undefined;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-16">Statut</TableHead>
          <TableHead>Description</TableHead>
          {isComparaisonModeles ? (
            notationsParModele.map((notation) => (
              <TableHead key={notation.model} className="w-24 text-center">
                {notation.model}
              </TableHead>
            ))
          ) : (
            <TableHead className="w-24 text-right">Points</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {criteres.map((critere) => (
          <TableRow
            key={critere.criteriaId}
            className={!critere.covered ? "opacity-60" : ""}
          >
            <TableCell>
              <IconeCouverture covered={critere.covered} />
            </TableCell>
            <TableCell className="font-medium">{critere.description}</TableCell>
            {isComparaisonModeles ? (
              notationsParModele.map((notation) => {
                const covered = notation.criteresCouverts[critere.criteriaId];
                return (
                  <TableCell key={notation.model}>
                    <div className="flex justify-center">
                      {covered === undefined ? (
                        <span className="text-ardoise">-</span>
                      ) : (
                        <IconeCouverture covered={covered} />
                      )}
                    </div>
                  </TableCell>
                );
              })
            ) : (
              <TableCell
                className={`text-right font-semibold ${
                  critere.covered
                    ? critere.points > 0
                      ? "text-etat-fait"
                      : "text-etat-erreur"
                    : "text-ardoise"
                }`}
              >
                {critere.points > 0 ? `+${critere.points}` : critere.points} pts
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Réponses du candidat ayant servi à déterminer la couverture des critères de
 * la question. Les échanges exclus de l'évaluation ne sont pas affichés : ils
 * n'ont joué aucun rôle dans le résultat.
 *
 * Le type de réponse vit ici (et non plus par critère) : une relance n'a lieu
 * que si le candidat était hors sujet, donc le 1er échange retenu est spontané
 * et les suivants sont des réponses à relance.
 */
function ReponsesCandidat({ exchanges }: { exchanges: EntStructExchange[] }) {
  const [isOpen, setIsOpen] = useState(false);

  const visibles = exchanges.filter((e) => !e.excluded_from_evaluation);
  if (visibles.length === 0) return null;

  return (
    <div className="mb-2 rounded-surface border bg-background/60">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
      >
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        )}
        <User className="h-3.5 w-3.5 shrink-0" />
        <span>
          Réponse{visibles.length > 1 ? "s" : ""} du candidat
          {visibles.length > 1 ? ` (${visibles.length})` : ""}
        </span>
      </button>
      {isOpen && (
        <div className="px-3 pb-2 pt-1 space-y-2">
          {visibles.map((exchange, idx) => {
            const qualite = libelleQualiteReponse(exchange.response_quality);
            return (
            <div key={idx} className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Badge
                  variant={idx === 0 ? "secondary" : "default"}
                  className="text-xs pointer-events-none"
                >
                  {idx === 0 ? "Spontané" : "Relance"}
                </Badge>
                {qualite && (
                  <Badge
                    variant="outline"
                    className={`text-xs pointer-events-none ${
                      qualite.ton === "alerte"
                        ? "text-etat-alerte border-etat-alerte"
                        : qualite.ton === "attention"
                          ? "text-etat-alerte border-etat-alerte-trait"
                          : ""
                    }`}
                  >
                    {qualite.label}
                  </Badge>
                )}
              </div>
              <p className="whitespace-pre-wrap break-words text-sm text-foreground/90">
                {exchange.candidate_text}
              </p>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CriteresParQuestion({
  criteres,
  questionsMap,
  exchangesMap,
  notationsParModele,
}: {
  criteres: CritereDetail[];
  questionsMap: Map<string, string>;
  // Échanges par questionId. Absent sur les évaluations antérieures à la clé
  // `exchanges` renvoyée par l'API : le bloc réponse est alors simplement omis.
  exchangesMap?: Map<string, EntStructExchange[]>;
  notationsParModele?: NotationParModele[];
}) {
  const groups: {
    questionId: string;
    questionText: string;
    criteres: CritereDetail[];
  }[] = [];
  const seen = new Map<string, number>();

  for (const critere of criteres) {
    if (!seen.has(critere.questionId)) {
      seen.set(critere.questionId, groups.length);
      groups.push({
        questionId: critere.questionId,
        questionText: questionsMap.get(critere.questionId) ?? "",
        criteres: [critere],
      });
    } else {
      groups[seen.get(critere.questionId)!]!.criteres.push(critere);
    }
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.questionId}>
          {group.questionText && (
            <p className="text-sm font-medium text-foreground/80 mb-2 px-1 py-1.5 bg-muted/60 rounded-surface border-l-2 border-primary/40 italic">
              {group.questionText}
            </p>
          )}
          {(() => {
            const exchanges = exchangesMap?.get(group.questionId);
            return exchanges?.length ? (
              <ReponsesCandidat exchanges={exchanges} />
            ) : null;
          })()}
          <TableCriteres
            criteres={group.criteres}
            notationsParModele={notationsParModele}
          />
        </div>
      ))}
    </div>
  );
}

function SousCompetenceCard({
  sousCompetence,
  questionsMap,
  exchangesMap,
  notationsParModele,
}: {
  sousCompetence: SousCompetenceScore;
  questionsMap: Map<string, string>;
  exchangesMap?: Map<string, EntStructExchange[]>;
  notationsParModele?: NotationParModele[];
}) {
  const [isOpen, setIsOpen] = useState(false);

  const pourcentage =
    sousCompetence.scoreMax > 0
      ? Math.round((sousCompetence.scoreObtenu / sousCompetence.scoreMax) * 100)
      : 0;

  if (
    sousCompetence.nom === "" ||
    (sousCompetence.nom === "Général" && sousCompetence.criteres.length === 0)
  ) {
    return null;
  }

  return (
    <div className="ml-4 mt-2 border-l-2 border-muted rounded-surface p-3 bg-muted/30">
      <div
        className="flex items-center justify-between cursor-pointer hover:bg-muted-50 transition-colors select-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="font-medium text-sm">{sousCompetence.nom}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block w-20 h-2 bg-trait rounded-full overflow-hidden">
            <div
              className="h-full bg-etat-info transition-all duration-300"
              style={{ width: `${pourcentage}%` }}
            />
          </div>
          <Badge
            variant="secondary"
            className="text-sm min-w-[80px] flex justify-center pointer-events-none"
          >
            {sousCompetence.scoreObtenu}/{sousCompetence.scoreMax}
          </Badge>
        </div>
      </div>
      {isOpen && (
        <div className="mt-3">
          <CriteresParQuestion
            criteres={sousCompetence.criteres}
            questionsMap={questionsMap}
            exchangesMap={exchangesMap}
            notationsParModele={notationsParModele}
          />
        </div>
      )}
    </div>
  );
}

export function CompetenceCard({
  competence,
  isHierarchical,
  questionsMap,
  exchangesMap,
  notationsParModele,
}: {
  competence: CompetenceScore;
  isHierarchical: boolean;
  questionsMap: Map<string, string>;
  exchangesMap?: Map<string, EntStructExchange[]>;
  notationsParModele?: NotationParModele[];
}) {
  const [isOpen, setIsOpen] = useState(false);

  const pourcentage =
    competence.scoreMax > 0
      ? Math.round((competence.scoreObtenu / competence.scoreMax) * 100)
      : 0;

  return (
    <Card>
      <CardHeader
        className="cursor-pointer hover:bg-muted/50 transition-colors select-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            {isOpen ? (
              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
            ) : (
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
            )}
            <div className="flex flex-col gap-1 min-w-0">
              <CardTitle className="text-lg">{competence.nom}</CardTitle>
              <Badge variant="secondary" className="text-xs w-fit">
                Poids: {competence.poids}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="hidden sm:block w-32 h-2 bg-trait rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${pourcentage}%` }}
              />
            </div>
            <Badge
              variant="outline"
              className="text-base px-3 py-1 min-w-20 flex justify-center pointer-events-none"
            >
              {pourcentage}%
            </Badge>
            <span className="text-sm text-muted-foreground whitespace-nowrap min-w-24 text-left">
              ({competence.scoreObtenu}/{competence.scoreMax} points)
            </span>
          </div>
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent className="pt-0">
          {isHierarchical && competence.sousCompetences ? (
            <div className="space-y-3">
              {competence.sousCompetences.map((sousComp, idx) => (
                <SousCompetenceCard
                  key={idx}
                  sousCompetence={sousComp}
                  questionsMap={questionsMap}
                  exchangesMap={exchangesMap}
                  notationsParModele={notationsParModele}
                />
              ))}
            </div>
          ) : null}
          {!isHierarchical && competence.criteres && (
            <CriteresParQuestion
              criteres={competence.criteres}
              questionsMap={questionsMap}
              exchangesMap={exchangesMap}
              notationsParModele={notationsParModele}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}

export function MessageAucuneDonnee() {
  return (
    <div className="p-8 text-center text-muted-foreground">
      <p>Aucune donnée de score disponible pour cet entretien.</p>
      <p className="text-sm mt-2">
        L'entretien n'a peut-être pas encore généré de données d'évaluation.
      </p>
    </div>
  );
}

function CheatingIndicator({ meta }: { meta: CheatingMeta }) {
  const interpretation = interpreterScore(meta.scoreSuspicion);

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-pastille text-xs ${interpretation.couleur} bg-muted`}
    >
      <AlertTriangle className="h-3 w-3" />
      <span>({interpretation.niveau})</span>
    </div>
  );
}

function CheatingTooltip({
  meta,
  anchorRect,
}: {
  meta: CheatingMeta;
  anchorRect: DOMRect;
}) {
  const interpretation = interpreterScore(meta.scoreSuspicion);

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: anchorRect.bottom + 4,
        right: window.innerWidth - anchorRect.right,
        zIndex: 9999,
      }}
      className="bg-popover border rounded-surface p-3 shadow-lg min-w-55 text-sm"
    >
      <div className="space-y-2">
        <div className="flex items-center justify-between font-semibold">
          <span className={interpretation.couleur}>
            {interpretation.niveau}
          </span>
          <span className="text-xs text-muted-foreground">
            {interpretation.description}
          </span>
        </div>
        <div className="border-t pt-2 space-y-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-3 w-3" />
            <span>Changements onglet: {meta.changementsOnglet}</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="h-3 w-3" />
            <span>
              Temps hors page: {(meta.tempsHorsPage / 1000).toFixed(1)}s
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Timer className="h-3 w-3" />
            <span>
              Temps rédaction: {(meta.tempsRedaction / 1000).toFixed(1)}s
            </span>
          </div>
          {meta.pasteDetecte && (
            <div className="flex items-center gap-2 text-etat-erreur font-medium">
              <ClipboardPaste className="h-3 w-3" />
              <span>Copier-coller détecté sur cette réponse</span>
            </div>
          )}
          {meta.vitesseFrappe > 10 && (
            <div className="flex items-center gap-2 text-etat-alerte">
              <Type className="h-3 w-3" />
              <span>
                Vitesse anormale: {meta.vitesseFrappe.toFixed(1)} car/s
              </span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function MessageBubble({ message }: { message: EntStructMessage }) {
  const isAssistant = message.role === "assistant";
  const [showTooltip, setShowTooltip] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const getMessageType = () => {
    if (isAssistant || !message.transcriptionMeta) return "text";
    const pct = message.transcriptionMeta.pourcentage;
    if (pct >= 80) return "voice";
    if (pct >= 20) return "mixed";
    return "text";
  };

  const messageType = getMessageType();

  return (
    <div
      className={`flex min-w-0 ${isAssistant ? "justify-start" : "justify-end"} mb-3`}
    >
      <div
        className={`max-w-[85%] min-w-0 p-3 rounded-2xl ${
          isAssistant
            ? "bg-muted text-muted-foreground"
            : "bg-primary/10 text-primary"
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          {isAssistant ? (
            <Bot className="h-4 w-4" />
          ) : (
            <User className="h-4 w-4" />
          )}
          <Badge
            variant={isAssistant ? "secondary" : "default"}
            className="text-xs pointer-events-none"
          >
            {isAssistant ? "Assistant" : "Candidat"}
          </Badge>
          {!isAssistant && (
            <>
              {messageType === "voice" && (
                <Badge
                  variant="default"
                  className="text-xs bg-etat-info ml-1 pointer-events-none"
                >
                  <Mic className="h-3 w-3 mr-1" /> Vocal
                </Badge>
              )}
              {messageType === "mixed" && (
                <Badge
                  variant="secondary"
                  className="text-xs ml-1 pointer-events-none"
                >
                  <Mic className="h-3 w-3 mr-1" />
                  Mixte ({message.transcriptionMeta?.pourcentage}%)
                </Badge>
              )}
              {messageType === "text" && !message.transcriptionMeta && (
                <Badge variant="outline" className="text-xs ml-1">
                  <Type className="h-3 w-3 mr-1" /> Texte
                </Badge>
              )}
              {message.cheatingMeta && (
                <div
                  ref={triggerRef}
                  className="ml-auto"
                  onMouseEnter={() => {
                    if (triggerRef.current) {
                      setAnchorRect(triggerRef.current.getBoundingClientRect());
                    }
                    setShowTooltip(true);
                  }}
                  onMouseLeave={() => {
                    setShowTooltip(false);
                    setAnchorRect(null);
                  }}
                >
                  <CheatingIndicator meta={message.cheatingMeta} />
                  {showTooltip && anchorRect && (
                    <CheatingTooltip
                      meta={message.cheatingMeta}
                      anchorRect={anchorRect}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <p className="whitespace-pre-wrap break-words text-sm">
          {message.content.replace(/ENT_FINISHED/g, "").trim()}
        </p>
      </div>
    </div>
  );
}

export function ConversationDropdown({
  reponses,
}: {
  reponses: Record<string, EntStructMessage>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const messages = getMessagesFromReponses(reponses);

  if (messages.length === 0) return null;

  return (
    <Card className="w-full">
      <CardHeader
        className="cursor-pointer hover:bg-muted/50 transition-colors select-none w-full"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isOpen ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )}
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">
              Transcription de l'entretien
            </CardTitle>
          </div>
          <Badge variant="outline" className="text-base px-3 py-1">
            {messages.length} messages
          </Badge>
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent className="overflow-hidden w-full">
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} />
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
